import React, { useEffect, useState, useRef } from 'react'
import { DatePicker, Input, Modal, Select } from 'antd'
import SubBookingDetail from './sub-booking-detail'
import { asyncGetSubBooking } from '../../../../services/booking.service'
import { BookingDto, TaskStatus } from '../../../../definition'
import PaddingComponent from '../../../components/paging.component'
import moment, { Moment } from 'moment'
import getTaskStatus from '../../../components/task-status.component'
import searchIcon from './../../../../styles/images/search-icon.png'
import { debounce } from 'lodash'
import { ArrowRightOutlined } from '@ant-design/icons'
import { DownLoadIcon } from '../../../../components/Icons'
import { ENV } from '../../../../initialization'
import SortBtns from '../../../components/sort-btns'
import { getQueryParams, updateUrlParam, deleteUrlParam } from '../../../../utils'

const { RangePicker } = DatePicker

interface SubBookingsProps {
  bookingId: string
}

export enum SortSubBookingField {
  BOOKING_NO = 'bookingNo',
  TIME_SLOT_BEGIN_AT = 'timsSlotBeginAt',
  INVOICE_NO = 'invoiceNo',
  TECHNICIAN = 'technician'
}

const limit = 10
const initStartTaskFilterDate = moment().startOf('month').toDate()
const initEndTaskFilterDate = moment().endOf('month').toDate()

const SubBookings = ({ bookingId }: SubBookingsProps) => {
  const [subBookings, setSubBookings] = useState<BookingDto[]>([])
  const [page, setPage] = useState(0)
  const [totalPage, setTotalPage] = useState(1)
  const [isModalVisible, setIsModalVisible] = useState(false)
  const [selectedBookingId, setSelectedBookingId] = useState<string>('')
  const [status, setStatus] = useState<TaskStatus | undefined>(undefined)
  const [time, setTime] = useState<{ startTime: Date; endTime: Date }>({
    startTime: initStartTaskFilterDate,
    endTime: initEndTaskFilterDate
  })
  const [search, setSearch] = useState<undefined | string>('')
  const [sortBy, setSortBy] = useState<SortSubBookingField>(SortSubBookingField.BOOKING_NO)
  const [isDesc, setIsDesc] = useState(false)

  const debounceOnSeaching = useRef(
    debounce((e: React.ChangeEvent<HTMLInputElement>) => {
      setSearch(e.target.value)
      setPage(0)
    }, 1000)
  ).current

  useEffect(() => {
    getSubBookings()
  }, [page, search, time.startTime, time.endTime, status])

  useEffect(() => {
    const queries = getQueryParams<{ subBookingId?: string }>()
    if (queries.subBookingId) {
      setSelectedBookingId(queries.subBookingId)
      setIsModalVisible(true)
    }
  }, [])

  const getSubBookings = async () => {
    const res = await asyncGetSubBooking(bookingId, {
      offset: page * limit,
      limit,
      startTime: time.startTime,
      endTime: time.endTime,
      search,
      status,
      sortBy,
      sortType: isDesc ? 'DESC' : 'ASC'
    })
    setSubBookings(res.items)
    setTotalPage(Math.floor(((res?.count || 0) - 1) / limit) + 1)
  }

  const handleSort = (isDesc: boolean, sortBy: string) => {
    setIsDesc(isDesc)
    setSortBy(sortBy as SortSubBookingField)
  }

  const resetSubBookings = () => {
    getSubBookings()
  }

  return (
    <div className='booking-contract-area'>
      <div className='booking-contract-wrapper'>
        <div className='booking-contract-search'>
          <div className='booking-contract-search-action'>
            <Select
              placeholder='All Statuses'
              allowClear
              value={status}
              className='booking-contract-select-status'
              options={[
                { label: 'New Booking', value: TaskStatus.CREATED },
                // { label: 'Reassign Required', value: TaskStatus.REASSIGN },
                { label: 'Assigned', value: TaskStatus.ASSIGNED },
                { label: 'On The Way', value: TaskStatus.ON_THE_WAY },
                { label: 'Task Started', value: TaskStatus.STARTED },
                { label: 'Finalising', value: TaskStatus.COMPLETED },
                { label: 'Completed', value: TaskStatus.DONE },
                { label: 'Reschedule Required', value: TaskStatus.RESCHEDULE },
                { label: 'Cancelled', value: TaskStatus.CANCELED }
              ]}
              onSelect={(value: TaskStatus) => setStatus(value)}
              onClear={() => setStatus(undefined)}
            />
            <RangePicker
              style={{ width: 240 }}
              value={[moment(time.startTime) || null, moment(time.endTime) || null]}
              picker={'date'}
              format={'DD/MM/YYYY'}
              onChange={(dates: null | (Moment | null)[], dateStrings: string[]) => {
                if (dates && dates[0] && dates[1]) {
                  const startDay = new Date(new Date(moment(dates[0]).startOf('day').toISOString()))
                  const endDay = new Date(new Date(moment(dates[1]).endOf('day').toISOString()))

                  setTime({
                    startTime: startDay,
                    endTime: endDay
                  })
                }
              }}
            />
            <div className='booking-contract-search-input'>
              <Input
                className='search-input'
                placeholder="Search code, customer's name"
                onChange={(event) => debounceOnSeaching(event)}
              />
              <img alt='search' className='search-icon' src={searchIcon} />
            </div>
          </div>
        </div>
        <div className='booking-contract-table'>
          <div className='booking-contract-table-header'>
            <div className='f2 havesort'>
              <SortBtns sortBy={SortSubBookingField.BOOKING_NO} value={sortBy} isDesc={isDesc} onSort={handleSort} />
              Booking Code
            </div>
            <div className='f1 havesort'>
              <SortBtns
                sortBy={SortSubBookingField.TIME_SLOT_BEGIN_AT}
                value={sortBy}
                isDesc={isDesc}
                onSort={handleSort}
              />
              Date
            </div>
            <div className='f2'>Time slot</div>
            <div className='f2'>Status</div>
            <div className='f3 havesort'>
              <SortBtns sortBy={SortSubBookingField.TECHNICIAN} value={sortBy} isDesc={isDesc} onSort={handleSort} />
              Assigned Employee
            </div>
            <div className='f2 havesort'>
              <SortBtns sortBy={SortSubBookingField.INVOICE_NO} value={sortBy} isDesc={isDesc} onSort={handleSort} />
              Invoice
            </div>
            <div style={{ width: '60px' }}></div>
          </div>
          {subBookings.map((booking, index) => {
            return (
              <div
                className={`booking-contract-table-row ${index % 2 === 0 ? 'row-separate' : ''}`}
                onClick={() => {
                  setSelectedBookingId(booking.id)
                  setIsModalVisible(true)
                  updateUrlParam({ add: { subBookingId: booking.id } })
                }}
              >
                <div className='f2'>
                  {booking.code}-{booking?.subBookingNo}
                </div>
                <div className='f1'>
                  {booking.tasks?.[0].timeSlotBeginAt
                    ? moment(booking.tasks?.[0].timeSlotBeginAt).format('DD/MM/YYYY')
                    : undefined}
                </div>
                <div className='f2'>
                  {booking.tasks?.[0].timeSlotBeginAt
                    ? moment(booking.tasks?.[0].timeSlotBeginAt).format('HH:mm')
                    : undefined}{' '}
                  -{' '}
                  {booking.tasks?.[0].timeSlotEndAt
                    ? moment(booking.tasks?.[0].timeSlotEndAt).format('HH:mm')
                    : undefined}
                </div>
                <div className='f2'>
                  <div
                    style={{
                      padding: '0 8px',
                      minWidth: 'min'
                    }}
                    className={`task-status ${getTaskStatus(booking.tasks?.[0].status).cssStatus}`}
                  >
                    {booking.tasks?.[0].status ? getTaskStatus(booking.tasks?.[0].status).statusLabel : undefined}
                  </div>
                </div>
                <div className='f3'>{booking?.tasks?.[0].assignee?.profile?.name}</div>
                <div className='f2'>
                  <div
                    style={{
                      textDecoration: 'underline',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8
                    }}
                  >
                    <div
                      onClick={() => {
                        document.location.href = `/finance-report/invoice/${booking?.invoice?.id}`
                      }}
                    >
                      {booking?.invoice?.code}
                    </div>
                    {booking?.invoice?.code && (
                      <div
                        onClick={() => {
                          window.open(
                            `${ENV.BACKEND_PREFIX_URL}/api/booking/invoice/download/${booking?.invoice?.id}`,
                            '_blank'
                          )
                        }}
                      >
                        <DownLoadIcon color='var(--background-1)' style={{ marginRight: '5px' }} />
                      </div>
                    )}
                  </div>
                </div>
                <div style={{ width: '60px' }} className='booking-action'>
                  <div className='action' onClick={() => setSelectedBookingId(booking.id)}>
                    <div style={{ marginRight: '4px' }}>View</div> <ArrowRightOutlined />
                  </div>
                </div>
              </div>
            )
          })}
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <PaddingComponent
            selectedPage={page}
            totalPage={totalPage}
            numberDisplayedIndex={limit}
            onSelectIndex={(page) => setPage(page)}
            className='customer-page-paging'
          />
        </div>
      </div>
      <Modal
        visible={isModalVisible}
        onCancel={() => {
          setIsModalVisible(false)
          deleteUrlParam('subBookingId')
        }}
        footer={null}
        width={1000}
        destroyOnClose={true}
        style={{
          zIndex: 1000
        }}
      >
        {selectedBookingId && (
          <SubBookingDetail
            bookingId={selectedBookingId}
            onSaveSuccess={() => {
              setIsModalVisible(false)
              deleteUrlParam('subBookingId')
              resetSubBookings()
            }}
            onClose={() => {
              setIsModalVisible(false)
              deleteUrlParam('subBookingId')
            }}
          />
        )}
      </Modal>
    </div>
  )
}

export default SubBookings
